import { Model, PModel, PropertyPath, ValidationResult } from '../engine.js';
import { Schema } from '../schema.js';

import { CompatibilityTable } from './CompatibilityTable.js';
import { TypeReference } from '../types.js';

import {
  castCompatibleValue,
  CompatibilityResult,
  CompatibleValue,
  isRestrictedTo,
  noRestrictions,
  restrictedTo
} from './CompatibleValues.js';

/***
 * CompatibilityService orchestrates compatibility rules specified in multiple CompatibilityTables
 */
export class CompatibilityService {
  public readonly tables = new Map<string, CompatibilityTable>();
  options = [];

  constructor(private schema: Schema) {
  }

  /***
   * Returns list of values that are registered for the propertyPath and potentially can be reached.
   * In case there is no compatibility rules for this property will return NoRestrictions.
   * @param propertyPath
   */
  getAvailableCompatibleValues(propertyPath: PropertyPath): CompatibilityResult {
    const values = new Set<CompatibleValue>();
    let compatibleValuesSets = 0;
    for (const table of this.tables.values()) {
      const tableAvailableCompatibleValues = table.getAvailableCompatibleValues(propertyPath);
      if (isRestrictedTo(tableAvailableCompatibleValues)) {
        compatibleValuesSets++;
        for (const value of tableAvailableCompatibleValues.values) {
          values.add(value);
        }
      }
    }
    if (compatibleValuesSets > 0) {
      return restrictedTo(castCompatibleValue([...values]))
    }
    return noRestrictions();
  }

  getCompatibleWithModelValues(model: Model, propertyPath: PropertyPath): CompatibilityResult {
    const values = new Set<CompatibleValue>();
    let compatibleValuesSets = 0;
    for (let table of this.tables.values()) {
      const compatibleWithModelValues = table.getCompatibleWithModelValues(model, propertyPath);
      if (isRestrictedTo(compatibleWithModelValues)) {
        compatibleValuesSets++;
        for (const value of compatibleWithModelValues.values) {
          values.add(value);
        }
      }
    }
    if (compatibleValuesSets > 0) {
      return restrictedTo(castCompatibleValue([...values]));
    } else {
      return noRestrictions();
    }
  }

  addCompatibilityTable(compatibilityTable: CompatibilityTable, tableName: string) {
    this.tables.set(tableName, compatibilityTable)
  }

  validate(model: Model): ValidationResult {
    for (const [tableName, table] of this.tables) {
      const error = table.validate(model)
      if (error !== null) {
        return ValidationResult.Error(`Incompatible model for ${tableName} table. ${error}`);
      }
    }
    return ValidationResult.Valid();
  }

  getCompatibilityInvolvedOptionPaths(model: PModel, typeRef: TypeReference): PropertyPath[] {
    const paths = new Set<PropertyPath>();
    const visitedTables = new Set<string>();
    for (const table of this.tables.values()) {
      if (table.typeRef === typeRef && !table.isCompatibilitySwitchedOff(model)) {
        for (let path of table.getCompatibilityInvolvedOptionPaths()) {
          paths.add(path);
        }
      }
    }
    return Array.from(paths);
  }
}

